import React from 'react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    cn
} from 'shared/lib/utils';
import {
    Plus, Trash2
} from 'lucide-react';
import {
    StepCommonAdvanced, StepCommonInlineFields, StepOelEditor,
    useProcedureStepIds
} from './_shared';
import ResourcePicker from '../../components/ResourcePicker';
import {
    LlmStepZone, LlmLabeled, LlmStepCanvas, llmFocusInputClass
} from './llmStepLayout';

const RUN_KIND_OPTIONS = [
    { value: 'step', label: 'Definition', hint: 'Definitions' },
    { value: 'flow', label: 'Flow', hint: 'Flows' }
];

/**
 * Pick which definition or flow runs when a choose case matches.
 */
function RunRefPicker({
    value, onChange, definitions, flows
}) {
    const kind = value?.kind || 'step';
    const ref = value?.ref || '';
    const pickerKind = kind === 'flow' ? 'flow' : 'definition';
    const localItems = kind === 'flow' ? flows : definitions;
    const kindMeta = RUN_KIND_OPTIONS.find((o) => o.value === kind) || RUN_KIND_OPTIONS[0];
    const selectClass = cn('h-8 w-full text-[12.5px]', llmFocusInputClass);

    return (
        <div className="space-y-2.5">
            <Select
                value={kind}
                onValueChange={(k) => onChange({
                    ...value,
                    kind: k,
                    ref: ''
                })}
            >
                <SelectTrigger className={selectClass} aria-label="Run target type">
                    <SelectValue placeholder="Pick target type" />
                </SelectTrigger>
                <SelectContent>
                    {RUN_KIND_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                            {o.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <p className="text-[10.5px] leading-snug text-muted-foreground">
                Pick from this opcode&apos;s{' '}
                <span className="font-medium text-foreground/80">{kindMeta.hint}</span>
                {' '}list below.
            </p>
            <ResourcePicker
                kind={pickerKind}
                value={ref}
                onChange={(id) => onChange({
                    ...value,
                    ref: id
                })}
                localItems={localItems}
                placeholder={`No ${kindMeta.label.toLowerCase()} selected`}
            />
        </div>
    );
}

function ChooseCaseCard({
    index, caseData, onChange, onRemove, definitions, flows, oelContext
}) {
    return (
        <div className="rounded-lg border border-indigo-100/80 bg-white px-3 py-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] font-semibold text-foreground">
                    Case {index + 1}
                </span>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onRemove}
                    className="h-7 px-2 text-muted-foreground hover:text-destructive"
                    aria-label={`Remove case ${index + 1}`}
                >
                    <Trash2 className="h-3.5 w-3.5" />
                </Button>
            </div>

            <LlmLabeled
                label="When (OEL)"
                help="Boolean expression — must evaluate to true/false. First matching case wins; later cases are skipped."
            >
                <StepOelEditor
                    value={caseData.when || ''}
                    onChange={(v) => onChange({ ...caseData, when: v })}
                    ctx={oelContext}
                    placeholder="@classify.output.result.confirmed == true"
                    expectedType="boolean"
                    ariaLabel={`Case ${index + 1} when expression`}
                />
            </LlmLabeled>

            <div className="border-t border-indigo-100/60 pt-3">
                <LlmLabeled
                    label="Run"
                    help="Which reusable definition or flow to execute when this case matches."
                >
                    <RunRefPicker
                        value={caseData.run || { kind: 'step', ref: '' }}
                        onChange={(r) => onChange({ ...caseData, run: r })}
                        definitions={definitions}
                        flows={flows}
                    />
                </LlmLabeled>
            </div>
        </div>
    );
}

/**
 * InlineChooseStep — kind:"choose".
 * Picks a step or flow to run based on which `case.when` matches first.
 */
export default function InlineChooseStep({
    step, onPatchStep, definitions = [], flows = [], oelContext
}) {
    const choose = step.choose || {
        cases: [],
        default: undefined
    };
    const cases = Array.isArray(choose.cases) ? choose.cases : [];
    const stepIdOptions = useProcedureStepIds(oelContext);

    function patchChoose(partial) {
        onPatchStep({
            ...step,
            choose: {
                ...choose,
                ...partial
            }
        });
    }
    function setCase(i, next) {
        patchChoose({
            cases: cases.map((c, j) => (j === i ? next : c))
        });
    }
    function addCase() {
        patchChoose({
            cases: [...cases, {
                when: '',
                run: {
                    kind: 'step',
                    ref: ''
                }
            }]
        });
    }
    function removeCase(i) {
        patchChoose({
            cases: cases.filter((_, j) => j !== i)
        });
    }

    return (
        <LlmStepCanvas>
            <LlmStepZone
                variant="configuration"
                title="Cases"
                description="Evaluated top-down — the first matching when-expression wins."
            >
                {cases.length === 0 ? (
                    <div className="rounded-md border border-dashed bg-white/60 px-3 py-2 text-[11.5px] text-muted-foreground">
                        No cases yet. Add at least one — the engine evaluates them top-down.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {cases.map((c, i) => (
                            <ChooseCaseCard
                                key={i}
                                index={i}
                                caseData={c}
                                onChange={(next) => setCase(i, next)}
                                onRemove={() => removeCase(i)}
                                definitions={definitions}
                                flows={flows}
                                oelContext={oelContext}
                            />
                        ))}
                    </div>
                )}
                <Button type="button" variant="outline" size="sm" onClick={addCase}>
                    <Plus className="mr-1 h-3 w-3" /> Add case
                </Button>
            </LlmStepZone>

            <LlmStepZone
                variant="output"
                title="Default"
                description="Optional fallback when no case when-expression is true."
            >
                <LlmLabeled
                    label="Default run"
                    help="Optional fallback when no case when-expression is true. Leave unset if the step should do nothing."
                >
                    <RunRefPicker
                        value={choose.default || { kind: 'step', ref: '' }}
                        onChange={(r) => patchChoose({ default: r })}
                        definitions={definitions}
                        flows={flows}
                    />
                </LlmLabeled>
            </LlmStepZone>

            <StepCommonInlineFields
                step={step}
                onPatchStep={onPatchStep}
                stepIdOptions={stepIdOptions}
            />

            <StepCommonAdvanced
                step={step}
                onPatchStep={onPatchStep}
                oelContext={oelContext}
            />
        </LlmStepCanvas>
    );
}
